import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useEffect, useMemo } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link as RouterLink, isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { usePageInfo } from '@/hooks/usePageInfo';

type ErrorDetails = {
    message: string;
    error_name?: string;
    error_stack?: string;
    status?: number;
    statusText?: string;
};

const describeError = (error: unknown): ErrorDetails => {
    if (isRouteErrorResponse(error)) {
        return {
            status: error.status,
            statusText: error.statusText,
            message: typeof error.data === 'string' ? error.data : error.statusText,
        };
    }
    if (error instanceof Error) {
        return {
            error_name: error.name,
            error_stack: error.stack,
            message: error.message,
        };
    }
    return { message: String(error) };
};

export const ErrorPage = () => {
    const error = useRouteError();
    const mixpanel = useMixpanel();
    const pageInfo = usePageInfo();
    const errorDetails = useMemo(() => describeError(error), [error]);

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.error(error);
    }, [error]);

    useEffect(() => {
        if (!mixpanel) {
            return;
        }
        mixpanel.track('Error Page Shown', {
            ...pageInfo,
            ...errorDetails,
        });
    }, [mixpanel, errorDetails, pageInfo]);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundImage:
                    'url(https://images.unsplash.com/photo-1488921618671-463b781ac428?q=80&w=2070&auto=format&fit=crop)',
            }}
        >
            <Container maxWidth="md">
                <Paper
                    sx={{
                        p: 4,
                        backgroundColor: grey[200],
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h1">Oops</Typography>
                    <Typography variant="h2">Sorry, an unexpected error has occurred.</Typography>
                    <Typography variant="body1">{errorDetails.statusText || errorDetails.message}</Typography>
                    <Button variant="contained" component={RouterLink} to="/" sx={{ maxWidth: 350 }}>
                        Take me back
                    </Button>
                </Paper>
            </Container>
        </Box>
    );
};
